import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { FILTERS_VIEWS, showsEveryFilter } from "./filterPresentation";
import { applyTranslateResultToForms } from "./translateResult";

/**
 * useSearchBarSearchLifecycle
 *
 * Owns one search started from the search bar: a translate round trip
 * (submitQuery) or a re-run of the last query (runExistingSearch). Only those
 * two move the filters panel, and never while it shows every filter.
 * @param {object} deps - Store-backed state, form actions and dispatchers.
 * @returns {object} - `{ clearQueryToken, searchBarSearchPending, submitQuery,
 * runExistingSearch }`.
 */
export const useSearchBarSearchLifecycle = ({
  resultPayload,
  criteriaActions,
  filtersActions,
  filterChoices,
  setNlSearchDates,
  resetSearchDatesToDefault,
  selectDateFilter,
  userTimezone,
  requestSearch,
  clearTranslate,
  translateRequest,
  filtersPopulated,
  lastSettledSearch,
  filtersView,
  setFiltersView,
}) => {
  const appliedResultRef = useRef(null);
  // Recovers comparison operators the translate endpoint drops (PLFM-4680).
  const lastQueryRef = useRef("");
  // Settlement is keyed on this id: the store's pending flag describes
  // whichever search touched it last (WCHK-3037).
  const searchBarSearchIdRef = useRef(null);
  // The query field empties on what the result wrote, not on what the forms
  // hold.
  const resultMappedRef = useRef(null);
  // Defers the view effect to the render after the forms were written.
  const [appliedResultToken, setAppliedResultToken] = useState(0);
  // Bumped to tell the search field to empty itself; 0 is "nothing to clear".
  const [clearQueryToken, setClearQueryToken] = useState(0);
  // The store's pending flag cannot stand in: a paginated search running
  // alongside clears it mid-round-trip (WCHK-3037).
  const [searchBarSearchPending, setSearchBarSearchPending] = useState(false);

  const beginSearchBarSearch = (mapped) => {
    const searchId = uuidv4();
    searchBarSearchIdRef.current = searchId;
    resultMappedRef.current = mapped;
    setSearchBarSearchPending(true);
    return searchId;
  };

  const releaseSearchBarSearch = () => {
    searchBarSearchIdRef.current = null;
    resultMappedRef.current = null;
    setSearchBarSearchPending(false);
  };

  useEffect(() => {
    if (!resultPayload || appliedResultRef.current === resultPayload) return;
    appliedResultRef.current = resultPayload;
    const mapped = applyTranslateResultToForms(resultPayload, {
      criteriaActions,
      filtersActions,
      filterChoices,
      setNlSearchDates,
      resetSearchDatesToDefault,
      selectDateFilter,
      timezone: userTimezone,
      originalQuery: lastQueryRef.current,
    });
    // The dispatches above are synchronous, so the epic already sees the forms.
    const searchId = beginSearchBarSearch(mapped);
    setAppliedResultToken((token) => token + 1);
    requestSearch({ searchId });
  }, [
    resultPayload,
    criteriaActions,
    filtersActions,
    filterChoices,
    setNlSearchDates,
    resetSearchDatesToDefault,
    selectDateFilter,
    userTimezone,
    requestSearch,
  ]);

  // Consumed once even when nothing was populated, so a pill filled in later
  // cannot let a stale result move the panel.
  const viewedResultTokenRef = useRef(0);
  useEffect(() => {
    if (appliedResultToken === 0) return;
    if (viewedResultTokenRef.current === appliedResultToken) return;
    viewedResultTokenRef.current = appliedResultToken;
    if (filtersPopulated && !showsEveryFilter(filtersView)) {
      setFiltersView?.(FILTERS_VIEWS.POPULATED);
    }
  }, [appliedResultToken, filtersPopulated, filtersView, setFiltersView]);

  useEffect(() => {
    const searchId = searchBarSearchIdRef.current;
    if (!searchId) return;
    // Somebody else's search came back.
    if (lastSettledSearch?.searchId !== searchId) return;
    const mapped = resultMappedRef.current;
    const failed = !!lastSettledSearch.failed;
    releaseSearchBarSearch();
    // A failure rules nothing out, so the panel stays put.
    if (failed) return;
    if (!showsEveryFilter(filtersView)) {
      setFiltersView?.(
        filtersPopulated ? FILTERS_VIEWS.POPULATED : FILTERS_VIEWS.COLLAPSED
      );
    }
    // The chips now carry the query; one that mapped nothing keeps its text.
    if (mapped && (mapped.mappedFilters || mapped.mappedDateRange)) {
      setClearQueryToken((token) => token + 1);
    }
  }, [lastSettledSearch, filtersPopulated, filtersView, setFiltersView]);

  // Clearing `translateResult` drops the field out of its success state first.
  const submitQuery = (query) => {
    if (!query) return;
    lastQueryRef.current = query;
    clearTranslate();
    translateRequest({ query });
  };

  // Re-runs the last query without translating it again; no result lands, so
  // the search is marked here instead.
  const runExistingSearch = () => {
    const searchId = beginSearchBarSearch(null);
    requestSearch({ searchId });
  };

  return {
    clearQueryToken,
    searchBarSearchPending,
    submitQuery,
    runExistingSearch,
  };
};

export default useSearchBarSearchLifecycle;
